<template>
    <div class="ho-timeline">
        <div v-show="!timelineReady">
            <Loader :image-path="imagePath"/>
        </div>
        <div v-show="timelineReady">
            <TimelineZoomControl ref="zoomControl" v-on:zoomIn="zoomIn()" v-on:zoomOut="zoomOut()" v-on:rangeChange="setRange" />
        </div>
        <div ref="timeline"></div>
    </div>
</template>

<script>
    import { Timeline } from "vis-timeline/peer"
    import { DataSet } from "vis-data"
    import axios from 'axios'
    import DateService from "../services/dateService"
    import TimelineItem from "./TimelineItem"
    import Vue from 'vue'
    import Loader from "./Loader";
    import TimelineZoomControl from "./TimelineZoomControl";
    import LocalizedStringService from "../services/localizedStringService";

    const dateService = new DateService();
    const localizedStringService = new LocalizedStringService();

    export default {
        components: {TimelineZoomControl, Loader},
        props: {
            hallitusUuid: String,
            rakenneElementit: Array,
            kohdeLinkit: Object,
            kohteidenLinkit: Object,
            ministeriot: Array,
            liittyvatKohdeUuids: Array,
            valitavoiteTyypit: Array,
            jsonApi: String,
            imagePath: String
        },
        data: function () {
            return {
                timelineReady: false,
                localeState: "",
                visTimeline: null,
            }
        },
        methods: {
            loadTimelineRange() {
                axios
                    .get(this.jsonApi + '/hankeikkuna-government-timeline/data/timelineRange', {params: {'hallitusUuid': this.hallitusUuid}})
                    .then(response => {
                        const timelineStart = dateService.apiDateToMoment(response.data[0]);
                        const timelineEnd = dateService.apiDateToMoment(response.data[1]);

                        this.createTimeline(timelineStart, timelineEnd);
                    })
                    .catch(() => this.dataLoadFailed = true)
            },
            createTimeline(timelineStart, timelineEnd) {
                if (this.visTimeline) {
                    this.visTimeline.destroy();
                }

                const mountPoint = this.$refs['timeline'];

                let dataSet = createTimelineItems(this.rakenneElementit, this.kohdeLinkit, this.kohteidenLinkit, this.ministeriot, this.valitavoiteTyypit, this.$translate.current);
                let timeline = new Timeline(mountPoint, dataSet, null, getTimelineOptions(timelineStart, timelineEnd, this));

                timeline.on('select', (properties => {

                    // Close other items
                    const selectedItemIds = properties.items;
                    for (let itemId in this.visTimeline.itemSet.items) {
                        let itemData = this.visTimeline.itemSet.items[itemId].data;
                        if (selectedItemIds.indexOf(itemId) === -1 && itemData.showDetails) {
                            itemData.showDetails = false;
                            this.visTimeline.itemsData.update(itemData);
                        }
                    }

                    this.visTimeline.redraw();

                    let visItem = properties.event.target.closest('.vis-item');

                    if (visItem) {
                        let itemPosition = visItem.style.transform;
                        let n = itemPosition.indexOf("(");
                        let n1 = itemPosition.indexOf(",");
                        let itemMinXposition = parseInt(itemPosition.slice(n+1, n1));
                        let itemMaxXPosition = itemMinXposition + visItem.clientWidth;
                        let timelineMaxXPosition = this.visTimeline.dom.centerContainer.clientWidth;

                        // Move window if item does not fit in it fully.
                        if (itemMinXposition < 0 || itemMaxXPosition > timelineMaxXPosition) {
                            const timelineWindow = this.visTimeline.getWindow();
                            const days = datediff(timelineWindow.start, timelineWindow.end);
                            const timelineWidth = this.visTimeline.dom.centerContainer.clientWidth;
                            const pixelsPerDay = timelineWidth / days;
                            const pixelsToMove = itemMinXposition < 0 ? itemMinXposition - 10 : itemMaxXPosition - timelineMaxXPosition + 10;
                            const daysToMove = pixelsToMove / pixelsPerDay;
                            const newStart = addDays(timelineWindow.start, daysToMove);
                            const newEnd = addDays(timelineWindow.end, daysToMove);

                            this.visTimeline.setWindow(newStart, newEnd, {animation: true})
                        }
                    }
                }));

                timeline.on('rangechange', (event) => {
                    this.$refs.zoomControl.setRange(event.start, event.end);
                });

                this.visTimeline = timeline;
            },
            onTimelineComplete() {
                this.timelineReady = true;
            },
            zoomIn() {
                this.visTimeline.zoomIn(0.6);
            },
            zoomOut() {
                this.visTimeline.zoomOut(0.6);
            },
            setRange(range) {
                this.visTimeline.setWindow(range.start, range.end, {animation: false});
            }
        },
        beforeMount: function () {
            this.localeState = this.$translate.current;
            this.loadTimelineRange();
        },
        beforeDestroy() {
            if (this.visTimeline) {
                this.visTimeline.destroy();
            }
        }
    }

    function getTimelineOptions(timelineStart, timelineEnd, hoTimeline) {
        const TimelineItemClass = Vue.extend(TimelineItem);

        return {
            align: 'left',
            locale: 'fi',
            onInitialDrawComplete: hoTimeline.onTimelineComplete,
            orientation: {
                axis: 'top',
                item: 'top'
            },
            min: timelineStart,
            max: timelineEnd.clone().add(6, 'year'),
            start: timelineStart,
            end: timelineEnd,
            margin: {
                axis: 30,
                item: 30
            },
            selectable: true,
            type: 'box',
            zoomable: true,
            zoomKey: 'ctrlKey',
            zoomMin: 31536000000, // = 365 * 24 * 60 * 60 * 1000 Max zoom in to one year
            zoomMax: timelineEnd.valueOf() - timelineStart.valueOf() + 63072000000, // Add two years = 2 * 365 * 24 * 60 * 60 * 1000,
            showCurrentTime: true,
            template: (item) => {
                const timelineitem = new TimelineItemClass({
                    propsData: {item: item}
                });
                timelineitem.$on('focus', function () {
                    if (hoTimeline.visTimeline.getSelection()[0] !== item.id) {
                        hoTimeline.visTimeline.setSelection(item.id);
                    }
                });
                timelineitem.$on("itemToggled", function(showDetails) {
                    let itemData = hoTimeline.visTimeline.itemSet.items[item.id].data;
                    itemData.showDetails = showDetails;
                    hoTimeline.visTimeline.itemsData.update(itemData);
                });

                return timelineitem.$mount().$el;
            }
        };
    }

    function getMilestones(hoRakenneElementit) {
        var milestones = [];
        hoRakenneElementit.forEach(rakenneElementti => {
            milestones = milestones.concat(rakenneElementti.valitavoitteet)
        });
        return milestones;
    }

    function getMilestoneDate(milestone) {
        return dateService.getMoment(milestone.tavoitevuosi, milestone.tavoitekuukausi, 15)
    }

    function getVastuuMinisteriot(ministeriot, vastuuministerioUuids) {
        let vastuuministeriot = []

        vastuuministerioUuids.find(function(vastuumministerioUuid) {
            ministeriot.find(function(ministerio) {
                if (ministerio.uuid === vastuumministerioUuid) {
                    return vastuuministeriot.push(ministerio)
                }
            });
        });

        return vastuuministeriot;
    }

    function getKohteidenLinkit(milestone, kohteidenLinkit, locale) {
        let linkit = [];
        milestone.liittyvatKohdeUuids.forEach(kohdeUuid => {
            const kohteenLinkit = kohteidenLinkit[kohdeUuid];
            if (kohteenLinkit && kohteenLinkit.length > 0) {
                linkit = linkit.concat(kohteenLinkit)
            }
        });
        return linkit.filter(linkki => {
            return !milestoneHasSimilarLinkki(milestone, linkki, locale);
        });


    }

    function milestoneHasSimilarLinkki(milestone, linkki, locale) {
        return milestone.linkit.find(milestoneLinkki => {
            return localizedStringService.localizedStringEquals(milestoneLinkki.nimi, linkki.nimi, locale);
        }) !== undefined;
    }

    function getMilestoneType(valitavoiteTyypit, valitavoiteTyyppiUuid) {
        let valitavoitteenTyyppi = "";
        valitavoiteTyypit.find(function(valitavoiteTyyppi) {
            if (valitavoiteTyyppi.uuid === valitavoiteTyyppiUuid) {
                return valitavoitteenTyyppi = valitavoiteTyyppi;
            }
        });
        return valitavoitteenTyyppi;
    }

    function createTimelineItems(rakenneElementit, kohdeLinkit, kohteidenLinkit, ministeriot, valitavoiteTyypit, locale) {

        let dataSet = new DataSet();
        const milestones = getMilestones(rakenneElementit);
        milestones.forEach(milestone => {
            const milestoneItem = {
                id: milestone.id,
                start: getMilestoneDate(milestone),
                milestone: milestone,
                milestoneType: getMilestoneType(valitavoiteTyypit, milestone.valitavoiteTyyppiUuid),
                kohdeLinkit: kohdeLinkit,
                kohteidenLinkit: getKohteidenLinkit(milestone, kohteidenLinkit, locale),
                vastuuministeriot: getVastuuMinisteriot(ministeriot, milestone.vastuuministerioUuids),
                osavastuuministeriot: getVastuuMinisteriot(ministeriot, milestone.osavastuuministerioUuids),
                showDetails: false
            };
            dataSet.add(milestoneItem);
        });

        return dataSet;
    }

    function datediff(first, second) {
        // Take the difference between the dates and divide by milliseconds per day.
        // Round to nearest whole number to deal with DST.
        return Math.round((second-first)/(1000*60*60*24));
    }

    function addDays(date, days) {
        var result = new Date(date);
        result.setDate(result.getDate() + days);
        return result;
    }

</script>

<style scoped>
    @import '../../node_modules/vis-timeline/dist/vis-timeline-graph2d.css';
</style>

<style lang="scss">
    //
    // Vis.js - Hankeikkuna customized styles
    //
    $black : #222;
    $lightgray : #f0f0f0;
    $gray : #979797;
    $darkgray : #505050;
    $lightblue : #DFE7F5;
    $blue : #365abd;

    .vis-timeline {
        border: 0;

        .vis-panel.vis-bottom,
        .vis-panel.vis-center,
        .vis-panel.vis-top { border-bottom: 1px solid transparent; }

        .vis-time-axis .vis-grid.vis-vertical { border-bottom: 1px solid transparent;  }

        .vis-panel {
            border: 1px solid transparent;
            overflow-x: hidden;
            overflow-y: visible;

            .vis-axis {
                .vis-item {
                    background-color: $darkgray;
                    border-color: $darkgray;

                    &.vis-selected {
                        background-color: $blue;
                        border-color: $blue;
                    }
                }
            }
        }

        .vis-shadow {
            display: none;
        }

        .vis-item-content {
            padding: 0;
            white-space: initial;
        }

        .vis-item {

            &.vis-line {
                border: 1px solid $darkgray;
            }

            &.vis-box {
                border: 2px solid $darkgray;
                max-width: 400px;

                @media (max-width: 768px) {
                    max-width: 200px;
                }
            }

            &.vis-dot {
                border-color: $darkgray;
                border-width: 8px;
                border-radius: 8px;
                margin-left: -1px;
            }

            &.vis-selected {
                &.vis-line {
                    border: 1px solid $blue;
                }

                &.vis-box {
                    border: 2px solid $blue;
                    max-width: 400px;

                    @media (max-width: 768px) {
                        max-width: 200px;
                    }
                }

                &.vis-dot {
                    border-color: $blue;
                }
            }
        }

        .vis-time-axis {

            .vis-grid {
                border: 0;

                &.vis-odd {
                    background: $lightblue;
                }
            }

            .vis-text {
                &.vis-major {
                    font-weight: 900;
                }

                &.vis-minor {
                    font-weight: 500;
                }
            }
        }
        .vis-current-time {
            background: red;
            width: 3px;
            z-index: 0;
        }
    }
</style>